import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Pencil, UserPlus } from "lucide-react";
import { Avatar } from "./Avatar";

const STATUS = {
  aktiv: { label: "Aktiv bruker", cls: "text-emerald-600 bg-emerald-500/10" },
  invitert: { label: "Invitert", cls: "text-amber-600 bg-amber-500/10" },
  manuell: { label: "Ikke bruker ennå", cls: "text-neutral-500 bg-neutral-100" },
};

export default function PersonerModal({ members, onEndre, onLukk }) {
  const [redigerer, setRedigerer] = useState(null);
  const [tittel, setTittel] = useState("");
  const [jobber, setJobber] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  const startRediger = (m) => { setRedigerer(m.id); setTittel(m.title || ""); };

  const lagre = async () => {
    if (jobber || !redigerer) return;
    setJobber(true);
    try {
      await onEndre(redigerer, { title: tittel });
      setRedigerer(null);
    } finally {
      setJobber(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4" data-testid="personer-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <div className="sak-modal relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl border border-[#E7E4DB]">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div>
            <h2 className="text-[15px] font-semibold text-neutral-900">Teamet</h2>
            <p className="text-[11.5px] text-neutral-400 mt-0.5">Eiere hentes fra brukerne i Event OS — inviter nye via Brukere-siden.</p>
          </div>
          <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="personer-lukk">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-3 max-h-[46vh] overflow-y-auto">
          {members.length === 0 && (
            <p className="text-[12.5px] text-neutral-400 py-4 text-center" data-testid="personer-tom">
              Ingen personer ennå — inviter den første via Brukere-siden.
            </p>
          )}
          {members.map((m) => {
            const s = STATUS[m.status] || STATUS.manuell;
            return (
              <div key={m.id} className="group flex items-center gap-3 py-2.5 border-t border-neutral-50 first:border-0" data-testid={`person-rad-${m.id}`}>
                <Avatar person={m} size={28} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-neutral-900 truncate">{m.name}</p>
                  {redigerer === m.id ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        value={tittel}
                        onChange={(e) => setTittel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") lagre(); if (e.key === "Escape") setRedigerer(null); }}
                        placeholder="Rolle/tittel"
                        maxLength={120}
                        autoFocus
                        className="text-[12px] border border-[#E7E4DB] rounded-lg px-2.5 py-1.5 bg-white flex-1 focus:outline-none focus:border-neutral-400"
                        data-testid="person-tittel-input"
                      />
                      <button type="button" onClick={lagre} disabled={jobber}
                        className="text-[11.5px] font-medium bg-neutral-900 text-white px-3 py-1.5 rounded-lg disabled:opacity-30"
                        data-testid="person-lagre">
                        Lagre
                      </button>
                    </div>
                  ) : (
                    <p className="text-[11px] text-neutral-400 truncate">
                      {[m.title, m.email].filter(Boolean).join(" · ") || "—"}
                    </p>
                  )}
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${s.cls}`} data-testid={`person-status-${m.id}`}>
                  {s.label}
                </span>
                {redigerer !== m.id && (
                  <button
                    type="button"
                    onClick={() => startRediger(m)}
                    title="Endre rolle/tittel"
                    className="p-1.5 text-neutral-300 hover:text-neutral-800 opacity-0 group-hover:opacity-100 transition-all"
                    data-testid={`person-rediger-${m.id}`}
                  >
                    <Pencil size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t border-neutral-100 bg-neutral-50/50 rounded-b-2xl flex items-center justify-between gap-3">
          <p className="text-[11.5px] text-neutral-400">Nye teammedlemmer legges til ved å invitere dem som brukere.</p>
          <Link
            to="/admin/brukere"
            onClick={onLukk}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-neutral-800 bg-white border border-[#E7E4DB] px-3 py-2 rounded-lg hover:bg-neutral-50 transition-colors whitespace-nowrap"
            data-testid="personer-til-brukere"
          >
            <UserPlus size={13} /> Inviter bruker
          </Link>
        </div>
      </div>
    </div>
  );
}
